// src/ConsumerApp/Screens/Account/views/OrdersMasterDetailView.js

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { theme } from '../../../../assets/theme/theme';

import {
  useGetOrdersByCustomer,
  useGetOrderById,
  useGetInvoicesByOrderId,
  useCancelOrder,
  useCreateCase,
} from '../../../api/orders/orders.hooks';
import { useGetDeliveryAddressById } from '../../../api/addresses/deliveryAddress.hooks';
import { useToast } from '../../../context/ToastContext';

// Subcomponents
import OrderCard from '../components/OrderCard';
import DeliveryGroupCard from '../components/DeliveryGroupCard';
import OrderBillSummaryCard from '../components/OrderBillSummaryCard';
import OrderHelpCard from '../components/OrderHelpCard';
import CancelOrderModal from '../components/CancelOrderModal';
import RaiseIssueModal from '../components/RaiseIssueModal';

// Icons
import Package from '../../../../assets/icons/Package.svg';
import MagnifyingGlass from '../../../../assets/icons/MagnifyingGlass.svg';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import Receipt from '../../../../assets/icons/Receipt.svg';
import ArrowsClockwise from '../../../../assets/icons/ArrowsClockwise.svg';
import ArrowLeft from '../../../../assets/icons/ArrowLeft.svg';

const RAW_BACKEND_URL = (process.env.REACT_APP_FORE_BRICKBUCKET || '').replace(/\/$/, '');
const BACKEND_BASE_URL = (RAW_BACKEND_URL.replace(/\/brickbucket$/, '') || 'https://backend.brickbucket.in');

export default function OrdersMasterDetailView() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id;

  const [activeTab, setActiveTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRaiseIssueOpen, setIsRaiseIssueOpen] = useState(false);

  // Queries & Mutations
  const { data: rawOrdersData, isLoading: isOrdersLoading, refetch: refetchOrders } = useGetOrdersByCustomer(customerId);
  const { data: rawOrderDetail, isLoading: isDetailLoading, refetch: refetchOrderDetail } = useGetOrderById(orderId);
  const { mutateAsync: fetchInvoicesMutation, isPending: isFetchingInvoices } = useGetInvoicesByOrderId();
  const { mutate: cancelOrderMutation, isPending: isCancelling } = useCancelOrder();
  const { mutate: createCaseMutation, isPending: isCreatingCase } = useCreateCase();

  const apiOrders = useMemo(() => {
    const list = rawOrdersData?.orders || rawOrdersData?.data || rawOrdersData || [];
    return Array.isArray(list) ? list : [];
  }, [rawOrdersData]);

  const order = useMemo(() => {
    if (!orderId) return {};
    const detail = rawOrderDetail?.order || rawOrderDetail?.data || rawOrderDetail;
    if (detail && typeof detail === 'object' && Object.keys(detail).length > 0) {
      return detail;
    }
    return (
      apiOrders.find(
        (o) =>
          String(o._id) === String(orderId) ||
          String(o.order_id) === String(orderId) ||
          String(o.salesorder_id) === String(orderId) ||
          String(o.salesorder_number) === String(orderId) ||
          String(o.quick_buy_order_number) === String(orderId) ||
          String(o.order_number) === String(orderId)
      ) || {}
    );
  }, [rawOrderDetail, apiOrders, orderId]);

  const deliveryAddressId = useMemo(() => {
    return (
      order?.delivery_address_id?._id ||
      order?.delivery_address_id ||
      order?.delivery_address?._id ||
      null
    );
  }, [order]);

  const { data: rawAddressData, isLoading: isAddressLoading } = useGetDeliveryAddressById(
    typeof deliveryAddressId === 'string' ? deliveryAddressId : null
  );

  const resolvedAddress = useMemo(() => {
    const fromApi = rawAddressData?.data || rawAddressData;
    if (fromApi && typeof fromApi === 'object' && Object.keys(fromApi).length > 0) return fromApi;
    if (order?.delivery_address && typeof order.delivery_address === 'object') return order.delivery_address;
    if (order?.address && typeof order.address === 'object') return order.address;
    return null;
  }, [rawAddressData, order]);

  const orderDisplayNumber = order.quick_buy_order_number || order.order_number || orderId || '';
  const isDelivered = (order.status || '').toLowerCase() === 'delivered';

  const totalItemsCount = useMemo(() => {
    const items = order.items || [];
    return items.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0) || items.length;
  }, [order]);

  const activeIssue = useMemo(() => {
    const issueCandidate = order.issue || order.case || order.return_case;
    if (issueCandidate && typeof issueCandidate === 'object' && (issueCandidate.issue_number || issueCandidate.case_number || issueCandidate._id)) {
      return issueCandidate;
    }
    return null;
  }, [order]);

  const hasExistingIssue = Boolean(activeIssue);

  const handleViewInvoice = async () => {
    const targetOrderId = order._id || order.order_id || orderId;
    if (!targetOrderId) {
      showToast('Order reference not found.', 'error');
      return;
    }

    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Loading Tax Invoice...</title>
            <style>
              body { font-family: sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background-color: #F8FAFC; color: #334155; }
              .loader { text-align: center; background: #FFFFFF; padding: 32px 40px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
            </style>
          </head>
          <body>
            <div class="loader"><h3>Fetching Tax Invoice...</h3><p>Please wait while your document is being prepared.</p></div>
          </body>
        </html>
      `);
    }

    try {
      const response = await fetchInvoicesMutation(targetOrderId);
      const invoiceList = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
      const targetInvoice = invoiceList[0];

      if (!targetInvoice || !Array.isArray(targetInvoice.files) || targetInvoice.files.length === 0) {
        if (newTab) newTab.close();
        showToast('Invoice details have not been generated for this order yet.', 'info');
        return;
      }

      const rawFilePath = targetInvoice.files[0];
      const cleanPath = rawFilePath.startsWith('/') ? rawFilePath.slice(1) : rawFilePath;
      const invoicePdfUrl = `${BACKEND_BASE_URL}/brickbucket/uploads/${cleanPath}`;

      if (newTab) newTab.location.href = invoicePdfUrl;
      else window.open(invoicePdfUrl, '_blank');
    } catch {
      if (newTab) newTab.close();
      showToast('Could not retrieve invoice document. Please try again.', 'error');
    }
  };

  const handleConfirmCancel = (reasonText) => {
    const targetOrderId = order._id || order.order_id || order.salesorder_id || orderId;
    const deliveryGroups = Array.isArray(order.delivery_groups) ? order.delivery_groups : [];
    const deliveryGroupIds = deliveryGroups.map((g) => g.delivery_group_id || g._id).filter(Boolean);

    const payload = {
      salesorder_id: targetOrderId,
      delivery_group_ids: deliveryGroupIds.length > 0 ? deliveryGroupIds : null,
      reason: reasonText,
    };

    cancelOrderMutation(payload, {
      onSuccess: () => {
        showToast('Your cancellation request has been processed.', 'success');
        setIsCancelModalOpen(false);
        refetchOrderDetail();
        refetchOrders();
        queryClient.invalidateQueries({ queryKey: ['ordersList', customerId] });
      },
      onError: (err) => {
        showToast(err?.response?.data?.message || err?.message || 'Unable to cancel this order. Please try again.', 'error');
      },
    });
  };

  const handleRaiseIssueSubmit = (issueData) => {
    const targetEntityId = order.order_id || order._id || orderId;

    const payload = {
      case_type: issueData.reason_code,
      description: issueData.description
        ? `${issueData.reason_label} - ${issueData.description}`
        : issueData.reason_label,
      priority: 'HIGH',
      entity_type: 'ORDER',
      entity_id: targetEntityId,
      owner_id: customerId || order.customer_id?._id || order.customer_id || '',
      remarks: `Issue reported by customer: ${issueData.reason_label}`,
      created_by: customerId,
    };

    createCaseMutation(payload, {
      onSuccess: (res) => {
        const caseNumber = res?.data?.case_number || res?.case_number || '';
        showToast(
          caseNumber
            ? `Case ${caseNumber} created successfully.`
            : 'Issue reported successfully.',
          'success'
        );
        setIsRaiseIssueOpen(false);
        refetchOrderDetail();
        queryClient.invalidateQueries({ queryKey: ['orderDetails', orderId] });
      },
      onError: (err) => {
        showToast(
          err?.response?.data?.message || err?.message || 'Failed to submit issue. Please try again.',
          'error'
        );
      },
    });
  };

  const ongoingCount = useMemo(() => apiOrders.filter((o) => o.is_live_order === true).length, [apiOrders]);
  const closedCount = useMemo(() => apiOrders.filter((o) => o.is_live_order === false).length, [apiOrders]);

  const displayedOrders = useMemo(() => {
    return apiOrders.filter((ord) => {
      if (activeTab === 'ONGOING' && !ord.is_live_order) return false;
      if (activeTab === 'CLOSED' && ord.is_live_order) return false;

      const orderType = ord.type || 'Buy Now';
      if (activeFilter !== 'All' && orderType !== activeFilter) return false;

      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const matchesNum = (ord.order_number || ord.salesorder_id || ord.quick_buy_order_number || '').toLowerCase().includes(q);
        const matchesItems = ord.items?.some((item) => (item.item_name || item.product_name || '').toLowerCase().includes(q));
        return matchesNum || matchesItems;
      }
      return true;
    });
  }, [apiOrders, activeTab, activeFilter, searchQuery]);

  return (
    <>
      <style>{`
        /* ── Standard Design System Circular Back Button (Consistent on Mobile & Desktop) ── */
        .bb-universal-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }

        .bb-universal-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }

        .bb-universal-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }

        /* ── Breakpoints for Mobile Edge-to-Edge Bar vs Desktop Header Row ── */
        .bb-mobile-edge-header {
          display: none;
        }

        .bb-desktop-header-row {
          display: flex;
        }

        @media (max-width: 900px) {
          .bb-desktop-header-row {
            display: none !important;
          }

          .bb-mobile-edge-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }

          .bb-mobile-content-container {
            padding: 14px 16px !important;
          }
        }

        .bb-get-help-pill-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #CBD5E1;
          background-color: #FFFFFF;
          border-radius: 999px;
          padding: 6px 14px;
          font-family: ${theme.typography.bodySM.fontFamily};
          font-size: 12.5px;
          font-weight: 700;
          color: #E9001E;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }

        .bb-get-help-pill-btn:active {
          background-color: #F8FAFC;
        }

        .bb-existing-issue-card {
          background-color: #FFFBEB;
          border: 1px solid #FDE68A;
          border-radius: 12px;
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
        }

        .bb-issue-top-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
        }

        .bb-issue-number-tag {
          font-family: ${theme.typography.bodySM.fontFamily};
          font-size: 12px;
          font-weight: 800;
          color: #92400E;
          letter-spacing: 0.3px;
        }

        .bb-issue-status-pill {
          background-color: #FEF3C7;
          border: 1px solid #FCD34D;
          color: #B45309;
          font-family: ${theme.typography.bodySM.fontFamily};
          font-size: 10.5px;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 6px;
          text-transform: uppercase;
        }

        .bb-issue-subtext {
          font-family: ${theme.typography.headingMD.fontFamily};
          font-size: 13.5px;
          font-weight: 700;
          color: #1E293B;
          margin: 0;
        }

        .bb-issue-description-box {
          font-family: ${theme.typography.bodySM.fontFamily};
          font-size: 12px;
          color: #64748B;
          line-height: 16px;
          background-color: #FFFFFF;
          border: 1px solid #FEF3C7;
          border-radius: 8px;
          padding: 8px 10px;
          margin-top: 4px;
        }
      `}</style>

      {orderId ? (
        <div>
          {/* Mobile Header for Order Details */}
          <div className="bb-mobile-edge-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className="bb-universal-circle-back-btn"
                onClick={() => navigate('/consumer/account/orders')}
                aria-label="Back to Orders"
              >
                <img src={ArrowLeft} alt="Back" />
              </button>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h2 style={{
                  fontFamily: theme.typography.headingMD.fontFamily,
                  fontSize: 16,
                  fontWeight: '800',
                  color: theme.colors.charcoal,
                  margin: 0,
                }}>
                  Order {orderDisplayNumber}
                </h2>
                
              </div>
            </div>
         
          </div>

          <div style={detailsStyles.wrapper} className="bb-mobile-content-container">
            {isDetailLoading && !order.order_number ? (
              <div style={detailsStyles.loadingContainer}>Fetching order details...</div>
            ) : (
              <>
                {/* Desktop Order Details Header */}
                <div style={detailsStyles.headerRow} className="bb-desktop-header-row">
                  <button
                    className="bb-universal-circle-back-btn"
                    onClick={() => navigate('/consumer/account/orders')}
                    type="button"
                    aria-label="Back to Orders"
                  >
                    <img src={ArrowLeft} alt="Back" />
                  </button>
                  <div>
                    <h2 style={detailsStyles.orderNumberTitle}>{orderDisplayNumber}</h2>
                    <span style={detailsStyles.orderDateSubtitle}>
                      {order.order_date || order.createdAt
                        ? new Date((order.order_date || order.createdAt).replace?.(' ', 'T') || (order.order_date || order.createdAt)).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                          })
                        : '--'}
                    </span>
                  </div>
                </div>

                {/* Delivering Address Card */}
                <div style={detailsStyles.addressCard}>
                  <div style={detailsStyles.addressCardHeader}>
                    <span style={detailsStyles.addressCardHeaderTitle}>
                      Delivering to:{' '}
                      {resolvedAddress?.customer_id?.customer_name || resolvedAddress?.customer_name || customer?.customer_name || 'Site Address'}
                    </span>
                  </div>
                  <div style={detailsStyles.addressCardContent}>
                    {isAddressLoading ? (
                      <span style={{ fontSize: 12, color: theme.colors.concrete }}>Locating site address...</span>
                    ) : resolvedAddress ? (
                      <>
                        <p style={detailsStyles.addressText}>
                          {[resolvedAddress.flat_number, resolvedAddress.building_name, resolvedAddress.area_sector_locality || resolvedAddress.complete_address, resolvedAddress.city, resolvedAddress.pincode].filter(Boolean).join(', ')}
                        </p>
                        <span style={detailsStyles.addressPhone}>
                          {resolvedAddress.contact_mobile ? `+91 ${resolvedAddress.contact_mobile}` : resolvedAddress?.customer_id?.mobile_number ? `+91 ${resolvedAddress.customer_id.mobile_number}` : ''}
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: theme.colors.concrete }}>Address provided at order placement.</span>
                    )}
                  </div>
                </div>

                {/* Delivery Groups */}
                {(Array.isArray(order.delivery_groups) && order.delivery_groups.length > 0) ? (
                  order.delivery_groups.map((group, gIdx) => (
                    <DeliveryGroupCard
                      key={group.delivery_group_id || gIdx}
                      group={group}
                      isRfq={order.type === 'RFQ' || order.type === 'Project Quote'}
                      orderStatus={order.status}
                      orderData={order}
                    />
                  ))
                ) : (
                  <DeliveryGroupCard
                    group={{
                      delivery_group_status: order.salesorder_status || order.status,
                      delivery_categories: [{ items: order.items || [] }],
                    }}
                    isRfq={order.type === 'RFQ' || order.type === 'Project Quote'}
                    orderStatus={order.status}
                    orderData={order}
                  />
                )}

                {/* Bill Summary */}
                <OrderBillSummaryCard order={order} />

                {/* Issue Management & Status */}
                {hasExistingIssue ? (
                  <div className="bb-existing-issue-card">
                    <div className="bb-issue-top-row">
                      <span className="bb-issue-number-tag">
                        {activeIssue.issue_number || activeIssue.case_number || 'Issue Reported'}
                      </span>
                      <span className="bb-issue-status-pill">
                        {activeIssue.status || 'UNDER REVIEW'}
                      </span>
                    </div>

                    <h5 className="bb-issue-subtext">
                      {activeIssue.subtext || activeIssue.case_type || 'Issue under investigation'}
                    </h5>

                    {activeIssue.description && (
                      <div className="bb-issue-description-box">
                        {activeIssue.description}
                      </div>
                    )}
                  </div>
                ) : isDelivered ? (
                  <button
                    type="button"
                    style={detailsStyles.raiseIssueBtn}
                    onClick={() => setIsRaiseIssueOpen(true)}
                  >
                    <img src={ArrowsClockwise} alt="Issue" style={{ width: 18, height: 18 }} />
                    <span>Raise an Issue / Return</span>
                  </button>
                ) : null}

                {/* Cancellation Action Strip */}
                {order.cancel_enabled === true && order.status !== 'Cancelled' ? (
                  <div
                    style={detailsStyles.cancelButtonStrip}
                    onClick={() => setIsCancelModalOpen(true)}
                  >
                    <div>
                      <span style={detailsStyles.cancelButtonTitle}>Cancel Order</span>
                      <span style={detailsStyles.cancelButtonSub}>I want to cancel this order</span>
                    </div>
                    <img src={CaretRight} alt="arrow" style={{ width: 14, height: 14 }} />
                  </div>
                ) : order.status === 'Cancelled' ? (
                  <div style={detailsStyles.cancelledNotice}>
                    This order has been cancelled.
                  </div>
                ) : null}

                {/* Invoice Action Strip */}
                {order.invoice_generated && (
                  <div
                    style={{
                      ...detailsStyles.invoiceButton,
                      opacity: isFetchingInvoices ? 0.7 : 1,
                      pointerEvents: isFetchingInvoices ? 'none' : 'auto',
                    }}
                    onClick={handleViewInvoice}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img src={Receipt} alt="Invoice" style={{ width: 20, height: 20 }} />
                      <span style={{ fontWeight: '700', fontSize: 13.5, color: '#2B2A29' }}>
                        {isFetchingInvoices ? 'Opening invoice...' : 'View invoice details'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: theme.colors.concrete, fontWeight: '600' }}>{orderDisplayNumber}</span>
                      <img src={CaretRight} alt="Arrow" style={{ width: 14, height: 14 }} />
                    </div>
                  </div>
                )}

                {/* Help with Order */}
                <OrderHelpCard orderNumber={orderDisplayNumber} />

                {/* Cancel Modal */}
                <CancelOrderModal
                  isOpen={isCancelModalOpen}
                  onClose={() => setIsCancelModalOpen(false)}
                  onConfirm={handleConfirmCancel}
                  isPending={isCancelling}
                  orderNumber={orderDisplayNumber}
                />

                {/* Raise Issue Modal */}
                <RaiseIssueModal
                  isOpen={isRaiseIssueOpen}
                  onClose={() => setIsRaiseIssueOpen(false)}
                  onSubmitIssue={handleRaiseIssueSubmit}
                  isSubmitting={isCreatingCase}
                  orderNumber={orderDisplayNumber}
                />
              </>
            )}
          </div>
        </div>
      ) : (
        /* Orders List Screen */
        <div>
          {/* Mobile Header for Orders List (Circular ArrowLeft Back to Account) */}
          <div className="bb-mobile-edge-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                type="button"
                className="bb-universal-circle-back-btn"
                onClick={() => navigate('/consumer/account', { replace: true })}
                aria-label="Back to Account"
              >
                <img src={ArrowLeft} alt="Back" />
              </button>
              <h2 style={{
                fontFamily: theme.typography.headingLG.fontFamily,
                fontSize: 18,
                fontWeight: '800',
                color: theme.colors.charcoal,
                margin: 0,
              }}>
                Orders
              </h2>
            </div>
          </div>

          <div style={listStyles.wrapper} className="bb-mobile-content-container">
            {/* Desktop Orders List Header */}
            <div style={listStyles.headerRow} className="bb-desktop-header-row">
              <h2 style={listStyles.pageTitle}>My Orders</h2>
            </div>

            <div style={listStyles.controls}>
              <div style={listStyles.searchContainer}>
                <img src={MagnifyingGlass} alt="search" style={{ width: 16, height: 16, opacity: 0.6 }} />
                <input
                  type="text"
                  placeholder="Search by order ID or item name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={listStyles.searchInput}
                />
                {searchQuery && (
                  <button style={listStyles.clearBtn} onClick={() => setSearchQuery('')}>
                    ✕
                  </button>
                )}
              </div>

              <div style={listStyles.toggleContainer}>
                <button
                  style={{ ...listStyles.toggleTab, ...(activeTab === 'ALL' ? listStyles.activeToggleTab : {}) }}
                  onClick={() => setActiveTab('ALL')}
                >
                  All
                </button>
                <button
                  style={{ ...listStyles.toggleTab, ...(activeTab === 'ONGOING' ? listStyles.activeToggleTab : {}) }}
                  onClick={() => setActiveTab('ONGOING')}
                >
                  Ongoing
                  {ongoingCount > 0 && <span style={listStyles.badge}>{ongoingCount}</span>}
                </button>
                <button
                  style={{ ...listStyles.toggleTab, ...(activeTab === 'CLOSED' ? listStyles.activeToggleTab : {}) }}
                  onClick={() => setActiveTab('CLOSED')}
                >
                  Closed ({closedCount})
                </button>
              </div>
            </div>

            {isOrdersLoading ? (
              <div style={listStyles.stateContainer}>Loading your orders...</div>
            ) : displayedOrders.length === 0 ? (
              <div style={listStyles.emptyBox}>
                <img src={Package} alt="empty" style={{ width: 80, height: 80, opacity: 0.25, marginBottom: 12 }} />
                <span style={{ ...theme.typography.headingMD, fontSize: 15, fontWeight: '700', color: theme.colors.charcoal }}>No orders found</span>
                <span style={{ ...theme.typography.bodySM, fontSize: 13, color: theme.colors.concrete, marginTop: 4 }}>
                  {searchQuery ? 'No results matched your search.' : 'You have no orders in this tab yet.'}
                </span>
              </div>
            ) : (
              <div style={listStyles.ordersList}>
                {displayedOrders.map((ord) => {
                  const key = ord.order_id || ord._id || ord.salesorder_id;
                  return (
                    <OrderCard
                      key={key}
                      order={ord}
                      onPress={() => navigate(`/consumer/account/orders/${key}`)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const listStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 16 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  pageTitle: {
    ...theme.typography.headingLG,
    fontSize: 22,
    color: theme.colors.charcoal,
    margin: 0,
    fontWeight: '800',
  },
  orderCountText: {
    ...theme.typography.bodySM,
    fontSize: 13,
    color: theme.colors.concrete,
    fontWeight: '600',
  },
  controls: { display: 'flex', flexDirection: 'column', gap: 12 },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: theme.radius[12],
    padding: '10px 14px',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 14,
    color: theme.colors.charcoal,
    background: 'transparent',
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: theme.colors.concrete,
    fontSize: 12,
  },
  toggleContainer: {
    display: 'flex',
    backgroundColor: '#F5F3EF',
    borderRadius: 24,
    padding: 3,
    border: '1px solid #FFFFFF',
    width: 'fit-content',
    gap: 4,
  },
  toggleTab: {
    border: 'none',
    background: 'none',
    padding: '6px 16px',
    borderRadius: 20,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    color: '#2E4057',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: '700',
  },
  activeToggleTab: {
    backgroundColor: '#FFFFFF',
    color: theme.colors.charcoal,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  badge: {
    backgroundColor: theme.colors.primaryRed || '#E9001E',
    color: '#FFFFFF',
    fontSize: 10,
    borderRadius: 10,
    padding: '2px 6px',
    fontWeight: '800',
  },
  ordersList: { display: 'flex', flexDirection: 'column', gap: 12 },
  stateContainer: {
    padding: 40,
    textAlign: 'center',
    color: theme.colors.concrete,
    fontSize: 14,
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  emptyBox: {
    padding: 48,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E2E8F0',
  },
};

const detailsStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 16 },
  headerRow: { display: 'flex', alignItems: 'center' },
  orderNumberTitle: {
    ...theme.typography.headingMD,
    fontSize: 18,
    color: theme.colors.charcoal,
    margin: 0,
    fontWeight: '800',
  },
  orderDateSubtitle: {
    ...theme.typography.bodySM,
    fontSize: 12,
    color: theme.colors.concrete,
    marginTop: 2,
    display: 'block',
  },
  addressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #CBD5E1',
    overflow: 'hidden',
  },
  addressCardHeader: {
    backgroundColor: '#F8F6F2',
    padding: '10px 16px',
    borderBottom: '1px solid #CBD5E1',
  },
  addressCardHeaderTitle: {
    ...theme.typography.bodySM,
    fontSize: 13,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  addressCardContent: { padding: 14 },
  addressText: {
    margin: '0 0 4px 0',
    fontSize: 13,
    color: theme.colors.charcoal,
    lineHeight: '18px',
    fontFamily: theme.typography.bodySM.fontFamily,
  },
  addressPhone: {
    fontSize: 12,
    color: theme.colors.concrete,
    fontFamily: theme.typography.bodySM.fontFamily,
  },
  cancelButtonStrip: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  cancelButtonTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: theme.colors.primaryRed || '#E9001E',
    display: 'block',
    fontFamily: theme.typography.bodySM.fontFamily,
  },
  cancelButtonSub: {
    fontSize: 11,
    color: theme.colors.concrete,
    display: 'block',
    marginTop: 2,
    fontFamily: theme.typography.bodySM.fontFamily,
  },
  cancelledNotice: {
    padding: '12px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    color: '#B91C1C',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: theme.typography.bodySM.fontFamily,
  },
  invoiceButton: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 12,
    padding: '14px 18px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  raiseIssueBtn: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid #CBD5E1',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 13.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  loadingContainer: {
    padding: 48,
    textAlign: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E2E8F0',
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
};